import type { Schema } from "./types";

export type Schemas = Record<string, Schema>;

export const refName = (ref: string) => ref.split("/").pop() ?? ref;

export type Resolved = {
  schema: Schema;
  /** Component name when the schema is (or wraps) a `$ref`. */
  name?: string;
};

const isObj = (v: unknown): v is Schema =>
  !!v && typeof v === "object" && !Array.isArray(v);

const deref = (schema: Schema, schemas: Schemas): Resolved => {
  if (typeof schema.$ref !== "string") return { schema };
  const name = refName(schema.$ref);
  const target = schemas[name];
  return target ? { schema: target, name } : { schema: {}, name };
};

/** Follows `$ref` and merges `allOf` members into one plain schema. */
export const resolve = (
  schema: Schema | undefined,
  schemas: Schemas,
  seen: ReadonlySet<string> = new Set(),
): Resolved => {
  if (!schema) return { schema: {} };
  const first = deref(schema, schemas);
  const base = first.schema;
  if (!Array.isArray(base.allOf)) return first;
  if (first.name && seen.has(first.name)) return { schema: {}, name: first.name };

  const next = first.name ? new Set(seen).add(first.name) : seen;
  const members = base.allOf.map((m: Schema) => resolve(m, schemas, next));
  const merged: Schema = { ...base };
  delete merged.allOf;
  merged.properties = {};
  merged.required = [];
  for (const m of members) {
    Object.assign(merged, stripStructure(m.schema));
    Object.assign(merged.properties, m.schema.properties ?? {});
    merged.required.push(...(m.schema.required ?? []));
  }
  if (base.nullable) merged.nullable = true;
  if (base.description) merged.description = base.description;
  if (merged.required.length === 0) delete merged.required;
  if (Object.keys(merged.properties).length === 0) delete merged.properties;
  if (!merged.type && merged.properties) merged.type = "object";

  const name = first.name ?? (members.length === 1 ? members[0]?.name : undefined);
  return { schema: merged, name };
};

const stripStructure = (s: Schema) => {
  const { properties: _p, required: _r, allOf: _a, ...rest } = s;
  return rest;
};

export const variantsOf = (schema: Schema): Schema[] | undefined => {
  const list = schema.oneOf ?? schema.anyOf;
  return Array.isArray(list) && list.length > 0 ? list : undefined;
};

const baseType = (schema: Schema): string | undefined => {
  const t = schema.type;
  if (Array.isArray(t)) return t.find((x) => x !== "null");
  return t;
};

const isNullable = (schema: Schema) =>
  schema.nullable === true ||
  (Array.isArray(schema.type) && schema.type.includes("null"));

export const typeName = (schema: Schema | undefined, schemas: Schemas): string => {
  if (!schema) return "any";
  const nullable = isNullable(schema);
  const inner = (() => {
    if (typeof schema.$ref === "string") return refName(schema.$ref);
    const variants = variantsOf(schema);
    if (variants) {
      return [...new Set(variants.map((v) => typeName(v, schemas)))].join(" | ");
    }
    if (Array.isArray(schema.allOf)) {
      const r = resolve(schema, schemas);
      if (r.name) return r.name;
      return "object";
    }
    const t = baseType(schema);
    if (t === "array") {
      const item = isObj(schema.items) ? typeName(schema.items, schemas) : "any";
      return item.includes(" | ") ? `(${item})[]` : `${item}[]`;
    }
    if (t) return t;
    if (schema.properties) return "object";
    return "any";
  })();
  return nullable && !inner.endsWith("| null") ? `${inner} | null` : inner;
};

/** Enum, default and format, appended below the description. */
export const notes = (schema: Schema): string[] => {
  const out: string[] = [];
  if (Array.isArray(schema.enum)) {
    out.push(`Possible values: ${schema.enum.map((v) => `\`${String(v)}\``).join(", ")}.`);
  }
  if (schema.default !== undefined) {
    out.push(`Defaults to \`${JSON.stringify(schema.default)}\`.`);
  }
  if (typeof schema.format === "string" && !["int32", "int64", "float", "double", "string"].includes(schema.format)) {
    out.push(`Format: \`${schema.format}\`.`);
  }
  return out;
};

const stringExample = (schema: Schema, name?: string) => {
  switch (schema.format) {
    case "date-time":
      return "2026-01-15T10:30:00Z";
    case "date":
      return "2026-01-15";
    case "email":
      return "customer@example.com";
    case "uuid":
      return "9b2f6c1e-4b1f-4e7a-9d3a-2f7c1e8b5a10";
    case "uri":
    case "url":
      return "https://example.com";
    default:
      return name ?? "string";
  }
};

// How many named schemas may nest inside one example. Past that, or when a
// schema repeats on the path, an object becomes `{}` and a list `[]`.
const EXAMPLE_DEPTH = 2;

const placeholder = (schema: Schema | undefined, schemas: Schemas): unknown => {
  if (!schema) return null;
  const r = resolve(schema, schemas);
  const t = baseType(r.schema) ?? (r.schema.properties ? "object" : undefined);
  if (t === "array") return [];
  if (t === "object" || variantsOf(r.schema)) return {};
  return null;
};

/** A JSON value that fits the schema, for the request and response cards. */
export const exampleOf = (
  schema: Schema | undefined,
  schemas: Schemas,
  name?: string,
  seen: ReadonlySet<string> = new Set(),
): unknown => {
  if (!schema) return null;
  if (schema.example !== undefined) return schema.example;
  if (Array.isArray(schema.examples) && schema.examples.length > 0) return schema.examples[0];
  if (schema.const !== undefined) return schema.const;
  if (schema.default !== undefined) return schema.default;

  if (typeof schema.$ref === "string") {
    const ref = refName(schema.$ref);
    if (seen.has(ref) || seen.size >= EXAMPLE_DEPTH) return placeholder(schema, schemas);
    return exampleOf(schemas[ref], schemas, name, new Set(seen).add(ref));
  }

  const variants = variantsOf(schema);
  if (variants) return exampleOf(variants[0], schemas, name, seen);

  if (Array.isArray(schema.allOf)) {
    const r = resolve(schema, schemas);
    if (r.name && (seen.has(r.name) || seen.size >= EXAMPLE_DEPTH)) return placeholder(schema, schemas);
    const next = r.name ? new Set(seen).add(r.name) : seen;
    return exampleOf(r.schema, schemas, name, next);
  }

  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const t = baseType(schema) ?? (schema.properties ? "object" : undefined);
  switch (t) {
    case "object": {
      const out: Record<string, unknown> = {};
      for (const [key, prop] of Object.entries(schema.properties ?? {})) {
        if (isObj(prop)) out[key] = exampleOf(prop, schemas, key, seen);
      }
      if (!schema.properties && isObj(schema.additionalProperties)) {
        out.key = exampleOf(schema.additionalProperties, schemas, "key", seen);
      }
      return out;
    }
    case "array":
      return isObj(schema.items) ? [exampleOf(schema.items, schemas, name, seen)] : [];
    case "string":
      return stringExample(schema, name);
    case "integer":
      return typeof schema.minimum === "number" ? schema.minimum : 1;
    case "number":
      return typeof schema.minimum === "number" ? schema.minimum : 1.5;
    case "boolean":
      return true;
    default:
      return null;
  }
};
